import type { ContainerListingEntry, StorageEntityType } from '../shared/types';

export type PlacementSeverity = 'root' | 'placed' | 'awaiting' | 'attention' | 'critical';

export interface ContainerPlacementStatus {
  severity: PlacementSeverity;
  label: string;
  issue: boolean;
  expectedParentType?: StorageEntityType;
  parentType?: StorageEntityType;
}

export interface InventoryHierarchyNode {
  entry: ContainerListingEntry;
  children: InventoryHierarchyNode[];
}

export interface InventoryHierarchyModel {
  roots: InventoryHierarchyNode[];
  detachedRoots: InventoryHierarchyNode[];
  issues: Array<{ entry: ContainerListingEntry; status: ContainerPlacementStatus }>;
}

export const EXPECTED_STORAGE_PARENT: Partial<Record<StorageEntityType, StorageEntityType>> = {
  SHELF: 'RACK',
  BOX: 'SHELF',
  ROW: 'BOX',
  BATCH: 'ROW',
};

const TYPE_LABEL: Record<StorageEntityType, string> = {
  RACK: 'Rack',
  SHELF: 'Shelf',
  BOX: 'Box',
  ROW: 'Row',
  BATCH: 'Batch',
};

const TYPE_ORDER: Record<StorageEntityType, number> = {
  RACK: 0,
  SHELF: 1,
  BOX: 2,
  ROW: 3,
  BATCH: 4,
};

export function storageEntityTypeFromId(value: string | undefined): StorageEntityType | undefined {
  if (value === undefined) return undefined;
  const match = /^tcvs:(rack|shelf|box|row|batch):/i.exec(value);
  if (match === null) return undefined;
  return match[1]?.toUpperCase() as StorageEntityType;
}

export function storageEntityShortId(entityId: string): string {
  const parts = entityId.split(':');
  return parts.length === 3 ? (parts[2] ?? entityId) : entityId;
}

export function storageEntityDisplayLabel(entry: ContainerListingEntry): string {
  switch (entry.entityType) {
    case 'RACK':
      return `Rack ${entry.rackCode ?? storageEntityShortId(entry.entityId)}`;
    case 'SHELF':
      return `Shelf ${entry.shelfCode ?? storageEntityShortId(entry.entityId)}`;
    case 'BOX':
      return `Box ${storageEntityShortId(entry.entityId)}`;
    case 'ROW':
      return `Row ${entry.rowCode ?? storageEntityShortId(entry.entityId)}`;
    case 'BATCH':
      return `Batch ${storageEntityShortId(entry.entityId)}`;
  }
}

export function classifyContainerPlacement(
  entry: ContainerListingEntry,
  knownEntities?: ReadonlyMap<string, ContainerListingEntry>,
): ContainerPlacementStatus {
  const expectedParentType = EXPECTED_STORAGE_PARENT[entry.entityType];
  if (expectedParentType === undefined) {
    return { severity: 'root', label: 'Hierarchy root', issue: false };
  }

  if (entry.parentId === undefined) {
    if (entry.entityType === 'BATCH' && entry.runState === 'OPEN') {
      return {
        severity: 'awaiting',
        label: 'Open run - awaiting putaway into a Row',
        issue: true,
        expectedParentType,
      };
    }
    const severity: PlacementSeverity =
      entry.entityType === 'ROW' || entry.entityType === 'BATCH' ? 'critical' : 'attention';
    return {
      severity,
      label: `Missing ${TYPE_LABEL[expectedParentType]}`,
      issue: true,
      expectedParentType,
    };
  }

  const parentType = storageEntityTypeFromId(entry.parentId);
  if (parentType !== expectedParentType) {
    return {
      severity: 'critical',
      label: `Invalid parent - requires ${TYPE_LABEL[expectedParentType]}`,
      issue: true,
      expectedParentType,
      ...(parentType !== undefined ? { parentType } : {}),
    };
  }

  if (knownEntities !== undefined && !knownEntities.has(entry.parentId)) {
    return {
      severity: 'critical',
      label: `${TYPE_LABEL[expectedParentType]} record is missing`,
      issue: true,
      expectedParentType,
      parentType,
    };
  }

  return {
    severity: 'placed',
    label: `In ${TYPE_LABEL[parentType]} ${storageEntityShortId(entry.parentId)}`,
    issue: false,
    expectedParentType,
    parentType,
  };
}

function compareEntries(a: ContainerListingEntry, b: ContainerListingEntry): number {
  const type = TYPE_ORDER[a.entityType] - TYPE_ORDER[b.entityType];
  if (type !== 0) return type;
  return storageEntityDisplayLabel(a).localeCompare(storageEntityDisplayLabel(b));
}

export function buildInventoryHierarchy(
  entries: readonly ContainerListingEntry[],
): InventoryHierarchyModel {
  const knownEntities = new Map(entries.map((entry) => [entry.entityId, entry]));
  const nodes = new Map<string, InventoryHierarchyNode>(
    entries.map((entry) => [entry.entityId, { entry, children: [] }]),
  );
  const roots: InventoryHierarchyNode[] = [];
  const detachedRoots: InventoryHierarchyNode[] = [];
  const issues: InventoryHierarchyModel['issues'] = [];

  for (const entry of entries) {
    const node = nodes.get(entry.entityId) as InventoryHierarchyNode;
    const status = classifyContainerPlacement(entry, knownEntities);
    if (status.issue) issues.push({ entry, status });

    if (entry.entityType === 'RACK') {
      roots.push(node);
      continue;
    }
    const parent = entry.parentId === undefined ? undefined : nodes.get(entry.parentId);
    if (status.severity === 'placed' && parent !== undefined) parent.children.push(node);
    else detachedRoots.push(node);
  }

  const sortNodes = (items: InventoryHierarchyNode[]): void => {
    items.sort((a, b) => compareEntries(a.entry, b.entry));
    items.forEach((item) => sortNodes(item.children));
  };
  sortNodes(roots);
  sortNodes(detachedRoots);
  issues.sort((a, b) => {
    const severity =
      Number(b.status.severity === 'critical') - Number(a.status.severity === 'critical');
    return severity !== 0 ? severity : compareEntries(a.entry, b.entry);
  });

  return { roots, detachedRoots, issues };
}
